import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useIsAuthenticated, useHasHydrated, setAuthRedirectCallback } from '@/stores/authStore';
import { useEffect } from 'react';

interface AuthGuardProps {
  children: ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useIsAuthenticated();
  const hasHydrated = useHasHydrated();

  // Register the redirect callback with the auth store
  // This allows API interceptors to trigger React Router navigation
  useEffect(() => {
    const handleRedirect = () => {
      window.location.href = '/login';
    };
    setAuthRedirectCallback(handleRedirect);
    return () => setAuthRedirectCallback(null);
  }, []);

  // Wait for hydration before checking auth
  // This prevents flash of redirect when store re-hydrates from localStorage
  if (!hasHydrated) {
    return (
      <div
        className="flex items-center justify-center h-screen bg-khiimori-bg-primary"
        role="status"
        aria-label="Loading authentication state"
        aria-live="polite"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}